import { Component, ElementRef, HostListener, Input, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { animate, style, transition, trigger } from '@angular/animations';

import { Observable, of } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';

import { BlockAction, ViewPart, ViewState } from '@core/models';
import { SubSink } from '@core/utils/subsink';
import { Block } from '@core/models/block/block.model';
import { BlockActionType } from '@core/enums';
import { ApiClientService, Slug } from '@core/services/client/api-client.service';
import { ButtonHandlerService } from '@core/services/button-handler.service';
import { ClientService } from '@core/services/client/client.service';
import { Page } from '@app/core/interfaces/page.interface';
import { SessionStorageService } from '@app/shared/services/storage';
import { MixedObject } from '@core/interfaces/mixed-object';

// ** The dropdown styles animations */
export const dropDownAnimations = trigger('dropDownAnimations', [
  transition(':enter', [
    style({opacity: 0}),
    animate('100ms', style({opacity: 1})),
  ]),
  transition(':leave', [
    animate('100ms', style({opacity: 0}))
  ])
]);

/**
 * Filter close action
 */
const filterRedirect = (action: BlockAction) => action.actionType === BlockActionType.REDIRECT;
const filterOpen = (action: BlockAction) => action.actionType === BlockActionType.OPEN;

@Component({
    selector: 'language-switcher',
    template: `
    <div #langDropdown class="language-dropdown" *ngIf="clientHasAdditionalLanguages">
      <div app-block
           *ngIf="dropDownToggleLanguages"
           [class]="dropDownToggleLanguages.uId"
           [item]="dropDownToggleLanguages"
           [appClickableBlock]="dropDownToggleLanguages">
      </div>
      <!-- (keydown.enter)="toggleDropDown()"  na potrzeby POLINU -->
      <div app-block
           class="drop-down"
           *ngIf="_isOpen"
           [@dropDownAnimations]
           [class]="dropDownDownLanguages.uId"
           [item]="dropDownDownLanguages">
      </div>
    </div>
  `,
    styleUrls: ['./language-switcher.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: [dropDownAnimations],
    providers: [ButtonHandlerService],
    standalone: false
})
export class LanguageSwitcherComponent implements OnInit, OnDestroy {
  @Input() specialBlock: Block;
  _isOpen = false;
  dropDownToggleLanguages: Block | undefined;
  dropDownDownLanguages: Block | undefined;
  buttonClick$: Observable<BlockAction> = this.buttonService.click$;
  clientHasAdditionalLanguages = false;
  @ViewChild('langDropdown', {read: ElementRef}) langDropdown: ElementRef;
  locale = this.storageService.getItem('language');
  private subSink = new SubSink();

  constructor(private router: Router,
              private apiClientService: ApiClientService,
              private clientService: ClientService,
              private location: Location,
              private route: ActivatedRoute,
              private storageService: SessionStorageService,
              private buttonService: ButtonHandlerService) {
  }

  // clicked outside the box
  @HostListener('document:click', ['$event'])
  documentClick(event) {
    if (this.langDropdown && !this.langDropdown.nativeElement.contains(event.target) && this._isOpen) {
      this._isOpen = false;
    }
  }

  ngOnInit() {
    this.initialize();
    // redirect subscription
    this.subSink.sink =
      this.buttonClick$
        .pipe(
          filter(filterRedirect),
          map(({actionData}) => actionData),
          switchMap((actionData: MixedObject) => {
            const lang = actionData.lang;
            const page: Page = this.route.snapshot.data.pageData.page;
            if (page.sharingShortLinks[lang]) {
              const slug: Slug = {slug: page.sharingShortLinks[lang]};
              return of(slug);
            }
            return of({slug: ''});
          }),
          tap((s: Slug) => this.location.go(s.slug))
        ).subscribe((s: Slug) => {
        this.router.navigateByUrl(s.slug);
      });

    // TODO: tu wali błędami jeśli nie ma dostępnych języków
    //  mało tego czasami po odświeżeniu strony tego obiektu nie ma,
    //  malo tego dostępne języki są również w subject pytanie czemu i czy tak powinno być
    this.clientHasAdditionalLanguages = (this.clientService?.client?.availableLocales ?? []).length > 1;
  }

  initialize() {
    const currentViewState = this.specialBlock.children
      .find(b => b.settings.viewState === ViewState.MAIN);

    this.dropDownToggleLanguages = currentViewState.children
      .find(b => b.settings.viewPart === ViewPart.DROP_DOWN_TOGGLE_LANGUAGES);

    this._isOpen = false;

    this.dropDownDownLanguages = currentViewState.children
      .find(b => b.settings.viewPart === ViewPart.DROP_DOWN_DOWN_LANGUAGES);

    this.subSink.sink =
      this.buttonClick$
        .pipe(
          filter(filterOpen))
        .subscribe(_ => this.toggleDropDown());
  }

  toggleDropDown() {
    this._isOpen = !this._isOpen;
  }

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }
}
